import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import RequiredAdditionalDocumentList from 'components/loan/RequiredAdditionalDocumentList';
import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import GuideMessage, { MessageType } from 'components/stateless/GuideMessage/GuideMessage';
import { BackHeaderTitle } from 'components/stateless/Title/BackHeaderTitle';
import { MULTIPLE_LOAN_REQUEST_STATUS } from 'enums';

import DealerBulkFinancingDetailAdditionalDocuments from './sections/additional-documents';
import DealerBulkFinancingDetailInformation from './sections/bulk-financing-information';
import DealerBulkFinancingDetailDesignatedBankAccountInformation from './sections/designated-bank-account-information';
import DealerBulkFinancingDetailFinancingList from './sections/financing-list';
import DealerBulkFinancingDetailFinancingRequestInformation from './sections/financing-request-information';
import useDealerBulkFinancingConfirmationDetailController from './useDealerBulkFinancingConfirmationDetailController';

function DealerBulkFinancingConfirmationDetail() {
  const { t } = useTranslation();
  const { multipleRequestLoansId } = useParams<any>();

  const {
    state,
    paginateRelatedFinancingList,
    relatedFinancingPageable,
    financingInfoCheckBoxState,
    bankAccountInfoCheckBoxState,
    isAuthorizerRequest,
    isOperatorRequest,
    checkedRequiredDocumentsIds,
    handleCancelButtonClick,
    handleSubmitFinancingApplicationButtonClick,
    handleRequiredAdditionalDocumentCheckboxChange,
  } = useDealerBulkFinancingConfirmationDetailController(multipleRequestLoansId);

  return (
    <>
      <BackHeaderTitle title={t('text:Bulk_Financing_Confirmation_Details')} />
      {isAuthorizerRequest && (
        <GuideMessage
          message={[
            t('text:For_bulk_financing_application_the_Requested_Disbursement_Date_is_applied_uniformly'),
            t('text:The_operator_has_requested_approval_for_the_financing_application_below'),
            t(
              'text:After_reviewing_the_financing_request_information_and_the_attached_files_approve_and_submit_the_financing_application_to_the_Financier',
            ),
          ]}
        />
      )}
      {state.bulkFinancingDetail.multipleLoanRequestStatus === MULTIPLE_LOAN_REQUEST_STATUS.CANCELLED && (
        <GuideMessage
          messageType={MessageType.ALERT}
          message={[
            t('text:The_financing_application_has_been_cancelled'),
            t('text:Please_check_the_reason_for_the_cancellation_below'),
          ]}
          reasonTitle={t('text:REASON_FOR_CANCELLATION')}
          reason={state.bulkFinancingDetail.cancelReason}
        />
      )}

      <DealerBulkFinancingDetailInformation bulkFinancingDetailInfo={state.bulkFinancingDetail} />
      <DealerBulkFinancingDetailFinancingRequestInformation
        bulkFinancingDetailInfo={state.bulkFinancingDetail}
        arList={state.bulkFinancingArList}
        isAuthorizerRequest={isAuthorizerRequest}
        financingInfoCheckBoxState={financingInfoCheckBoxState}
      />

      <DealerBulkFinancingDetailDesignatedBankAccountInformation
        bulkFinancingDetailInfo={state.bulkFinancingDetail}
        isAuthorizerRequest={isAuthorizerRequest}
        bankAccountInfoCheckBoxState={bankAccountInfoCheckBoxState}
      />
      <DealerBulkFinancingDetailAdditionalDocuments
        multipleLoanRequestId={multipleRequestLoansId}
        loanEtcAttachmentListData={state.bulkFinancingDetail.loanEtcAttachment}
      />
      <RequiredAdditionalDocumentList
        requiredAdditionalDocuments={state.requiredAdditionalDocuments}
        isRenderConfirmCheckbox={isAuthorizerRequest}
        checkedRequiredDocumentsIds={checkedRequiredDocumentsIds}
        handleRequiredAdditionalDocumentCheckboxChange={handleRequiredAdditionalDocumentCheckboxChange}
      />

      <DealerBulkFinancingDetailFinancingList
        financingList={state.bulkFinancingRelatedFinancingList}
        paginate={paginateRelatedFinancingList}
        pageable={relatedFinancingPageable}
      />

      <div className="content-area flex-end mt-3">
        {isOperatorRequest && (
          <Button
            size={ButtonSizeEnum.LG}
            onClick={handleCancelButtonClick}
            color={ButtonColorEnum.SECONDARY}
            variant={ButtonVariantEnum.OUTLINED}
          >
            {t('text:Cancel')}
          </Button>
        )}
        {isAuthorizerRequest && (
          <>
            <Button
              size={ButtonSizeEnum.LG}
              onClick={handleCancelButtonClick}
              className="me-2"
              color={ButtonColorEnum.SECONDARY}
              variant={ButtonVariantEnum.OUTLINED}
            >
              {t('text:Cancel')}
            </Button>
            <Button size={ButtonSizeEnum.LG} onClick={handleSubmitFinancingApplicationButtonClick}>
              {t('text:Submit_Financing_Application')}
            </Button>
          </>
        )}
      </div>
    </>
  );
}

export default DealerBulkFinancingConfirmationDetail;
